import React, { memo } from 'react';

import Image from 'next/image';
import LegacyImage from 'next/legacy/image';

import IconLabel from '@components/ui/labels/icon/IconLabel';
import { PlayCircleIcon, Setur, SeturSmall } from '@assets/icons';
import { useDevice } from '@contexts/device';
import ImageWithFallback from '@components/utils/ImageWithFallback';
import { cloudineryImage } from '@components/views/hotel/detail/utils/features';
import { HotelDetailVideoViewModel } from '@api/v3';
import CustomIcon from '@components/utils/CustomIcon';
import Typography from '@components/Typography';
import IconLabelNext from '@components/ui/labels/icon/IconLabelNext';

import {
  StyledCol,
  CheckmarkIconWrapper,
  MainImageWrapper,
  LastImage,
  ViewGalleryButton,
  StyledRow,
  VideoIcon,
  CertificateWrapper,
} from './GalleryHeroImagesStyles';

export interface Images {
  url?: string;
  alt?: string;
}

export interface GalleryHeroImagesProps {
  images: Images[];
  isSuggested?: boolean;
  isSeturExecutive?: boolean;
  isSafe?: boolean;
  totalImages: number;
  viewGallery?: (index?: number) => void;
  isBadge?: boolean;
  videos?: HotelDetailVideoViewModel[];
  videoViewGallery?: (index?: number) => void;
  setActiveGalleryModalName?: (string?: any) => void;
  isVideo?: boolean;
  tourismCertificateNumber?: string;
  tourismCertificateText?: string;
  tour?: boolean;
  badges?: any;
  detail?: boolean;
}

const GalleryHeroImages = ({
  images,
  isSeturExecutive,
  totalImages,
  viewGallery,
  isSuggested,
  videoViewGallery,
  setActiveGalleryModalName,
  isVideo,
  tourismCertificateNumber,
  tourismCertificateText,
  tour,
  badges,
  detail,
}: GalleryHeroImagesProps) => {
  const { isTablet, isDesktop } = useDevice();
  const md = isTablet || isDesktop;
  const openGalleryHandler = () => setTimeout(() => viewGallery);

  return (
    <div>
      <CertificateWrapper>
        {tourismCertificateNumber && tourismCertificateText ? (
          isDesktop ? (
            <Typography color='D300' variant='caption3' weight='Regular'>
              {`Kültür ve Turizm Bakanlığı - ${tourismCertificateText}: ${tourismCertificateNumber}`}
            </Typography>
          ) : (
            <Typography
              color='D300'
              variant='caption3'
              weight='Regular'
              style={{ marginLeft: '10px' }}
            >
              {`Kültür ve Turizm Bakanlığı - ${tourismCertificateText}: ${tourismCertificateNumber}`}
            </Typography>
          )
        ) : null}
      </CertificateWrapper>

      <StyledRow
        onClick={(e) => {
          e.stopPropagation();
          viewGallery?.(0);
        }}
      >
        <MainImageWrapper>
          <ImageWithFallback
            priority={true}
            Component={LegacyImage}
            width={820}
            height={404}
            src={cloudineryImage(
              images?.[0]?.url ?? '/static/images/setur-static.jpg',
              '820',
              '404'
            )}
            alt={images?.[0]?.alt ?? 'Setur Görseli Hazırlanmakta'}
          />
          {isVideo ? (
            <VideoIcon
              onClick={
                tour
                  ? (e) => {
                      e.stopPropagation();
                      viewGallery?.(0);
                    }
                  : (e) => {
                      e.stopPropagation();
                      setActiveGalleryModalName?.('Videolar');
                      videoViewGallery?.();
                    }
              }
            >
              <CustomIcon icon={PlayCircleIcon} fontSize={40} />
            </VideoIcon>
          ) : null}
          {tour ? (
            <CheckmarkIconWrapper>
              <IconLabelNext
                animationWidth={134}
                animationHeight={34}
                textStyle={{ fontWeight: 600, fontSize: 12 }}
                badgeAnimation={badges}
                icon={SeturSmall}
                iconSize={22}
                detail={detail}
              />
            </CheckmarkIconWrapper>
          ) : (
            <>
              {isSeturExecutive && !isSuggested ? (
                <CheckmarkIconWrapper>
                  <IconLabel
                    textStyle={{ fontWeight: 600, fontSize: 12 }}
                    text='Sadece SETUR’da'
                    icon={Setur}
                    iconSize={26}
                  />
                </CheckmarkIconWrapper>
              ) : null}
              {isSeturExecutive && isSuggested ? (
                <CheckmarkIconWrapper>
                  <IconLabel
                    textStyle={{ fontWeight: 600, fontSize: 12 }}
                    text='Sadece SETUR’da'
                    description='SETUR Öneriyor'
                    icon={Setur}
                    iconSize={26}
                  />
                </CheckmarkIconWrapper>
              ) : null}
              {!isSeturExecutive && isSuggested ? (
                <CheckmarkIconWrapper>
                  <IconLabel
                    textStyle={{ fontWeight: 600, fontSize: 12 }}
                    text='SETUR Öneriyor'
                    icon={Setur}
                    iconSize={26}
                  />
                </CheckmarkIconWrapper>
              ) : null}
            </>
          )}
        </MainImageWrapper>

        {md ? (
          <StyledCol>
            <ImageWithFallback
              Component={Image}
              width={400}
              height={192}
              src={cloudineryImage(
                images?.[1]?.url ?? '/static/images/setur-static.jpg',
                '400',
                '192'
              )}
              alt={images?.[1]?.alt ?? 'Setur Görseli Hazırlanmakta'}
              onClick={(e) => {
                e.stopPropagation();
                images[1] ? viewGallery?.(1) : viewGallery?.(0);
              }}
            />
            <LastImage>
              <ImageWithFallback
                Component={Image}
                width={400}
                height={192}
                src={cloudineryImage(
                  images?.[2]?.url ?? '/static/images/setur-static.jpg',
                  '400',
                  '192'
                )}
                alt={images?.[2]?.alt ?? 'Setur Görseli Hazırlanmakta'}
                onClick={(e) => {
                  e.stopPropagation();
                  images[2] ? viewGallery?.(2) : viewGallery?.(0);
                }}
              />
            </LastImage>
          </StyledCol>
        ) : null}
        <ViewGalleryButton onClick={openGalleryHandler}>
          Tüm Fotoğrafları Gör ({totalImages})
        </ViewGalleryButton>
      </StyledRow>
    </div>
  );
};

export default memo(GalleryHeroImages);
